import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Shield,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  ArrowLeft,
  LogOut,
  ChevronRight,
  Search,
  Filter,
  Eye,
  Activity,
  Scale,
  FileText,
  Brain,
  Gavel
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GovernanceDashboard = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [filter, setFilter] = useState({
    status: '',
    domain: '',
    agent: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const fetchDecisions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20'
      });
      if (filter.status) params.append('decision', filter.status);
      if (filter.domain) params.append('domain', filter.domain);
      if (filter.agent) params.append('agent_name', filter.agent);
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(`${API_URL}/api/admin/governance/decisions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDecisions(data.decisions || []);
        setHasMore(data.has_more || false);
      }
    } catch (error) {
      console.error('Failed to fetch governance decisions:', error);
      toast.error('Failed to load governance decisions');
    } finally {
      setLoading(false);
    }
  }, [token, filter, page, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/governance/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch governance stats:', error);
    }
  }, [token]);

  const fetchDecisionDetails = async (decisionId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/governance/decisions/${decisionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedDecision(data);
      }
    } catch (error) {
      console.error('Failed to fetch decision details:', error);
    }
  };

  useEffect(() => {
    fetchDecisions();
    fetchStats();
  }, [fetchDecisions, fetchStats]);

  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'approved':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'deferred':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'blocked_jurisdiction':
        return <Scale className="w-5 h-5 text-red-500" />;
      case 'blocked_risk':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'blocked_anomaly':
        return <AlertTriangle className="w-5 h-5 text-purple-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getDecisionBadge = (decision) => {
    const colors = {
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      deferred: 'bg-amber-100 text-amber-800',
      blocked_jurisdiction: 'bg-red-100 text-red-800',
      blocked_risk: 'bg-orange-100 text-orange-800',
      blocked_anomaly: 'bg-purple-100 text-purple-800',
      blocked_precondition: 'bg-gray-100 text-gray-800'
    };
    return (
      <Badge className={colors[decision] || 'bg-gray-100 text-gray-800'}>
        {decision?.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      low: 'bg-gray-100 text-gray-700',
      medium: 'bg-blue-100 text-blue-700',
      high: 'bg-orange-100 text-orange-700',
      critical: 'bg-red-100 text-red-700'
    };
    return <Badge className={colors[severity] || 'bg-gray-100'}>{severity}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="governance-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/autonomy" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Gavel className="w-6 h-6" />
                  Governance Decisions
                </h1>
                <p className="text-sm text-gray-500">Audit trail for all autonomous actions</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => { fetchDecisions(); fetchStats(); }} variant="outline" size="sm" data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={handleLogout} variant="ghost" size="sm">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 text-center">
                <Activity className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.total || 0}</p>
                <p className="text-xs text-gray-500">Total Decisions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.approved || 0}</p>
                <p className="text-xs text-gray-500">Approved</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.rejected || 0}</p>
                <p className="text-xs text-gray-500">Rejected</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Scale className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.blocked || 0}</p>
                <p className="text-xs text-gray-500">Blocked</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Brain className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.llm_checked || 0}</p>
                <p className="text-xs text-gray-500">LLM Checked</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <Filter className="w-5 h-5 text-gray-400" />
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Search decisions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                  data-testid="search-input"
                />
              </div>
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="border rounded-md px-3 py-2 text-sm"
                data-testid="filter-status"
              >
                <option value="">All Decisions</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="deferred">Deferred</option>
                <option value="blocked_jurisdiction">Blocked (Jurisdiction)</option>
                <option value="blocked_risk">Blocked (Risk)</option>
                <option value="blocked_anomaly">Blocked (Anomaly)</option>
              </select>
              <select
                value={filter.domain}
                onChange={(e) => setFilter({ ...filter, domain: e.target.value })}
                className="border rounded-md px-3 py-2 text-sm"
                data-testid="filter-domain"
              >
                <option value="">All Domains</option>
                <option value="arrears">Arrears</option>
                <option value="compliance">Compliance</option>
                <option value="maintenance">Maintenance</option>
                <option value="communications">Communications</option>
                <option value="legal">Legal</option>
              </select>
              <select
                value={filter.agent}
                onChange={(e) => setFilter({ ...filter, agent: e.target.value })}
                className="border rounded-md px-3 py-2 text-sm"
                data-testid="filter-agent"
              >
                <option value="">All Agents</option>
                <option value="james">JAMES</option>
                <option value="emma">EMMA</option>
                <option value="alex">ALEX</option>
                <option value="denise">DENISE</option>
                <option value="sarah">SARAH</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Decisions List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-2 space-y-4">
            {loading ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                  <p className="mt-2 text-gray-500">Loading decisions...</p>
                </CardContent>
              </Card>
            ) : decisions.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No governance decisions found</p>
                </CardContent>
              </Card>
            ) : (
              decisions.map((decision) => (
                <Card
                  key={decision.id}
                  className={`cursor-pointer transition-all ${
                    selectedDecision?.id === decision.id ? 'ring-2 ring-blue-500' : 'hover:shadow-md'
                  }`}
                  onClick={() => fetchDecisionDetails(decision.id)}
                  data-testid={`decision-${decision.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {getDecisionIcon(decision.decision)}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium capitalize">{decision.domain}</span>
                            <span className="text-gray-400">→</span>
                            <span className="text-blue-600">{decision.action_type}</span>
                            {getDecisionBadge(decision.decision)}
                          </div>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {decision.decision_rationale}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 flex-wrap">
                            {decision.agent_name && (
                              <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
                                {decision.agent_name.toUpperCase()}
                              </span>
                            )}
                            {decision.severity && getSeverityBadge(decision.severity)}
                            <span>{decision.jurisdiction}</span>
                            <span>{new Date(decision.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}

            {/* Pagination */}
            {(hasMore || page > 1) && (
              <div className="flex justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="px-4 py-2">Page {page}</span>
                <Button
                  variant="outline"
                  disabled={!hasMore}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedDecision ? (
              <Card className="sticky top-20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Decision Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
                  {/* Decision Status */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Decision</h4>
                    <div className="flex items-center gap-2 mt-1">
                      {getDecisionIcon(selectedDecision.decision)}
                      {getDecisionBadge(selectedDecision.decision)}
                    </div>
                  </div>

                  {/* Action Info */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Action</h4>
                    <p className="text-lg font-semibold capitalize">
                      {selectedDecision.domain} → {selectedDecision.action_type}
                    </p>
                  </div>

                  {/* Rationale */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Rationale</h4>
                    <p className="text-sm">{selectedDecision.decision_rationale}</p>
                  </div>

                  {/* Risk Score */}
                  {selectedDecision.risk_score !== undefined && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Risk Score</h4>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              selectedDecision.risk_score <= 0.3 ? 'bg-green-500' :
                              selectedDecision.risk_score <= 0.6 ? 'bg-yellow-500' :
                              selectedDecision.risk_score <= 0.8 ? 'bg-orange-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${selectedDecision.risk_score * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">
                          {(selectedDecision.risk_score * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Rule Checks */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Rule Checks</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Jurisdiction</span>
                        {selectedDecision.jurisdiction_check_passed ? (
                          <Badge className="bg-green-100 text-green-700">✓ Passed</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">✗ Failed</Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Risk Assessment</span>
                        {selectedDecision.risk_check_passed ? (
                          <Badge className="bg-green-100 text-green-700">✓ Passed</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">✗ Failed</Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Anomaly Detection</span>
                        {selectedDecision.anomaly_check_passed ? (
                          <Badge className="bg-green-100 text-green-700">✓ Passed</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">✗ Failed</Badge>
                        )}
                      </div>
                      {selectedDecision.llm_check_used && (
                        <div className="flex items-center justify-between text-sm">
                          <span>LLM Compliance</span>
                          <Badge className="bg-purple-100 text-purple-700">Used</Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rules Evaluated */}
                  {selectedDecision.rules_evaluated?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Rules Evaluated</h4>
                      <div className="flex flex-wrap gap-1">
                        {selectedDecision.rules_evaluated.map((rule, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {rule}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Proposal Data */}
                  {selectedDecision.proposal_data && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Proposal Details</h4>
                      <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-40">
                        {JSON.stringify(selectedDecision.proposal_data, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="pt-4 border-t">
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">Rulepack:</span>
                        <span className="ml-1">{selectedDecision.rulepack_version}</span>
                      </div>
                      <div>
                        <span className="font-medium">Agent:</span>
                        <span className="ml-1">{selectedDecision.agent_name?.toUpperCase() || '-'}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="font-medium">Created:</span>
                        <span className="ml-1">
                          {new Date(selectedDecision.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Eye className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Select a decision to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default GovernanceDashboard;
